import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  OnInit,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  NonNullableFormBuilder,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { Title } from '@angular/platform-browser';
import { filter } from 'rxjs';

// Services
import { AuthService } from '../services/auth/auth.service';
import { LoginErrorHandlerService } from '../services/auth/login-error-handler.service';
import { LanguageService } from '../services/language/language.service';
import { SeoService } from '../services/seo/seo.service';

// Models
import { User } from '../models/user.model';
import {
  LoginAttempt,
  LoginType,
} from '../services/auth/models/auth-login.models';
import { MicrosoftProfile } from '../services/auth/models/auth-microsoft-login.models';

// Translate
import { TranslocoService, TranslocoPipe } from '@jsverse/transloco';

// Social login
import {
  SocialAuthService,
  SocialUser,
  GoogleSigninButtonDirective,
} from '@abacritt/angularx-social-login';

// Crypt
import { hashPassword, hashThirdPartyId } from '../services/auth/auth-hash';

import { PrivateRoutes } from '../config/config.routes';
import { MixpanelService } from '../services/analytics/mixpanel.service';
import { mixpanelEvent } from '../config/mixpanel-events';

/**
 * Declare plugins so then you can run it
 */
declare function init_plugins(): void;

/**
 * Component to manage a login page
 */
@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    RouterLink,
    ReactiveFormsModule,
    GoogleSigninButtonDirective,
    TranslocoPipe,
  ],
})
export class LoginComponent implements OnInit {
  private readonly socialAuthService = inject(SocialAuthService);
  private readonly authService = inject(AuthService);
  private readonly loginErrorHandler = inject(LoginErrorHandlerService);
  private readonly languageService = inject(LanguageService);
  private readonly transloco = inject(TranslocoService);
  private readonly mixpanel = inject(MixpanelService);
  private readonly seoService = inject(SeoService);
  private readonly title = inject(Title);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);

  private readonly language = this.languageService.getLanguage();
  //* If the user comes from another URL and was redirected to the login
  private readonly redirectUrl =
    inject(ActivatedRoute).snapshot.queryParamMap.get('redirectUrl') ||
    PrivateRoutes.HOME;

  readonly loading = signal(false);

  private readonly rememberedEmail = localStorage.getItem('email') ?? '';
  readonly form = inject(NonNullableFormBuilder).group({
    email: [this.rememberedEmail, [Validators.required, Validators.email]],
    password: ['', Validators.required],
    rememberUser: [this.rememberedEmail.trim().length > 1],
  });

  constructor() {
    this.socialAuthService.authState
      .pipe(filter(Boolean), takeUntilDestroyed())
      .subscribe((googleUser) => {
        // authState is a ReplaySubject: clear it so a re-created Login/Register
        // doesn't replay this user into a login or sign up nobody clicked
        this.socialAuthService.signOut().catch(() => undefined);
        this.login(this.fromGoogleUser(googleUser), 'GOOGLE');
      });
  }

  ngOnInit() {
    init_plugins();

    // SEO
    const title = this.transloco.translate('COMMON.TITLE');
    const description = this.transloco.translate('COMMON.DESCRIPTION');
    this.title.setTitle(title);
    this.seoService.generateTags({
      title: title,
      description: description,
      slug: 'sign-in',
    });
  }

  /**
   * Email/password sign in
   */
  loginWithLab4u() {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const { email, password, rememberUser } = this.form.getRawValue();
    const user = new User();
    user.email = email.toLowerCase();
    user.language = this.language;
    user.login_type = 'LAB4U';
    user.password = hashPassword(user.email, password);

    this.login(user, 'LAB4U', rememberUser);
  }

  loginWithMicrosoft() {
    this.loading.set(true);
    this.authService
      .microsoftLogin()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (profile) =>
          this.login(this.fromMicrosoftProfile(profile), 'MICROSOFT'),
        error: (err) => this.onLoginError(err, { loginType: 'MICROSOFT' }),
      });
  }

  /**
   * Function to change language localStorage
   * @param language Variable to save the actual language
   */
  changeLanguage(language: string) {
    if (language === 'es' || language === 'en') {
      this.languageService.applyLanguage(language);
      window.location.reload();
    }
  }

  private login(user: User, loginType: LoginType, rememberUser = false) {
    this.loading.set(true);
    this.authService
      .login(user, rememberUser)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.triggerSignInSucceededEvent(loginType);
          this.navigateAfterLogin();
        },
        error: (err) =>
          this.onLoginError(err, { email: user.email, loginType }),
      });
  }

  private navigateAfterLogin() {
    this.router
      .navigateByUrl(this.redirectUrl)
      .then((navigated) => {
        if (!navigated) this.loading.set(false);
      })
      .catch(() => this.loading.set(false));
  }

  private onLoginError(err: unknown, attempt: LoginAttempt) {
    this.loading.set(false);
    this.loginErrorHandler.handle(err, attempt);
  }

  /** The backend expects the whole Google SocialUser enriched with our fields */
  private fromGoogleUser(googleUser: SocialUser): User {
    const user: User = googleUser;
    user.language = this.language;
    user.login_type = 'GOOGLE';
    user.image = googleUser.photoUrl;
    user.third_party_id = hashThirdPartyId(googleUser.id);
    return user;
  }

  private fromMicrosoftProfile(profile: MicrosoftProfile): User {
    const user = new User();
    user.email = profile.email;
    user.language = this.language;
    user.login_type = 'MICROSOFT';
    user.third_party_id = profile.graphId;
    return user;
  }

  private triggerSignInSucceededEvent(loginType: LoginType) {
    this.mixpanel.track(mixpanelEvent.SIGN_IN, {
      source_name: mixpanelEvent.SOURCE_NAME,
      feature_name: 'Miscelanea',
      view_name: 'Auth',
      event_version: 1,
      event_id: 298,
      event_type: 'Success',

      login_type: loginType,
    });
  }
}
